// 星牛管理端路由。由 router/index.js 在 G-UI-6 追加的 ops 两行之后以两行并入（import + routes.push），本文件之外恒不改上游路由。
//
// G-U1（2026-09-26，裁决《界面裁剪与流程融合 v1》+ U1-0 摸底裁定）：星牛账号只见星牛壳，不进 AICON 侧栏——
// - 管理端从 AICON MainLayout 搬进星牛桌面壳 XnAdminShell，路径迁到 /ops/admin/*，左侧 = 正式流程五步：
//   ① 参考片 learn → ② 脚本校正 calibrate（实例分镜 calibrate/:instanceId）→ ③ 模板上架 publish → ④ 出片与判定 runs（引擎全景 runs/engine/:runId）→ ⑤ 账 ledger；
// - 守卫同 /ops：逐条 beforeEnter（requireOpsLogin，没登录去 /ops/login?redirect=…），恒不写 meta.requiresAuth / requiresGuest（全局守卫会送去 AICON 的 /login、/dashboard）；
// - 旧 /xingniu/admin/* 逐条重定向到新路径（书签、线上旧链接照样能进）；「模板草稿」下线 → 落到管理端首页；
// - 首页同名覆盖：{ path: '/', name: 'Home' } 指向 /ops。vue-router 的 addRoute 遇到同名的根路由会先删掉旧的（createRouter 按数组顺序逐条 addRoute），
//   本文件在 router/index.js 里排在上游 '/' → '/dashboard' 之后并入，所以不改上游文件就把首页交给星牛壳（全站生效：这套部署只给星牛用）。
// - U1-1b（裁决《U1-1 核收》§3-③）：AICON 壳里的旧运营端四屏（G-UI-3，router/index.js 里 /xingniu 的四个具名子路由）与 /ops 重复——
//   同名根路由逐条顶替成重定向到 /ops（同一个 addRoute 机制：同名先删旧的），零上游改动；旧链接与书签照进 /ops，不再露 AICON 侧栏。
// 管理员身份：壳读 GET /me 的 is_admin 决定给不给管理端；后端 /admin/*、/engine/* 仍逐接口 403。
import { requireOpsLogin } from '@/router/ops'

const XnAdminShell = () => import('@/components/xingniu/XnAdminShell.vue')
const XnAdminLearn = () => import('@/views/xingniu/admin/XnAdminLearn.vue')
const XnAdminCalibrate = () => import('@/views/xingniu/admin/XnAdminCalibrate.vue')
const XnAdminInstance = () => import('@/views/xingniu/admin/XnAdminInstance.vue')
const XnAdminTemplates = () => import('@/views/xingniu/admin/XnAdminTemplates.vue')
const XnAdminRuns = () => import('@/views/xingniu/admin/XnAdminRuns.vue')
const XnAdminPanorama = () => import('@/views/xingniu/admin/XnAdminPanorama.vue')
const XnAdminMetrics = () => import('@/views/xingniu/admin/XnAdminMetrics.vue')

const guarded = (route) => ({ ...route, beforeEnter: requireOpsLogin })

export default [
  { path: '/', name: 'Home', redirect: '/ops' },
  {
    path: '/ops/admin',
    component: XnAdminShell,
    children: [
      // 首页：壳读 /engine/guide 后换到第一个没做完的步
      guarded({ path: '', name: 'XingniuAdminHome', component: { render: () => null }, meta: { title: '星牛 · 管理端' } }),
      guarded({ path: 'learn', name: 'XingniuAdminLearn', component: XnAdminLearn, meta: { title: '星牛 · 管理端 · ① 参考片', step: 'learn' } }),
      guarded({ path: 'calibrate', name: 'XingniuAdminCalibrate', component: XnAdminCalibrate, meta: { title: '星牛 · 管理端 · ② 脚本校正', step: 'calibrate' } }),
      guarded({ path: 'calibrate/:instanceId', name: 'XingniuAdminInstance', component: XnAdminInstance, props: true, meta: { title: '星牛 · 管理端 · 实例分镜', step: 'calibrate' } }),
      guarded({ path: 'publish', name: 'XingniuAdminTemplates', component: XnAdminTemplates, meta: { title: '星牛 · 管理端 · ③ 模板上架', step: 'publish' } }),
      guarded({ path: 'runs', name: 'XingniuAdminRuns', component: XnAdminRuns, meta: { title: '星牛 · 管理端 · ④ 出片与判定', step: 'runs' } }),
      guarded({ path: 'runs/engine/:runId', name: 'XingniuAdminPanorama', component: XnAdminPanorama, props: true, meta: { title: '星牛 · 管理端 · 运行全景', step: 'runs' } }),
      guarded({ path: 'ledger', name: 'XingniuAdminMetrics', component: XnAdminMetrics, meta: { title: '星牛 · 管理端 · ⑤ 账', step: 'ledger' } }),
      { path: ':rest(.*)+', redirect: '/ops/admin' }
    ]
  },
  // 旧路径（G-UI-8 / G-V1）→ 新路径
  { path: '/xingniu/admin', redirect: '/ops/admin' },
  { path: '/xingniu/admin/drafts', redirect: '/ops/admin' },
  { path: '/xingniu/admin/metrics', redirect: '/ops/admin/ledger' },
  { path: '/xingniu/admin/learn', redirect: '/ops/admin/learn' },
  { path: '/xingniu/admin/templates', redirect: '/ops/admin/publish' },
  { path: '/xingniu/admin/templates/instances/:instanceId', redirect: (to) => `/ops/admin/calibrate/${encodeURIComponent(to.params.instanceId)}` },
  { path: '/xingniu/admin/templates/runs/:runId', redirect: (to) => `/ops/admin/runs/engine/${encodeURIComponent(to.params.runId)}` },
  { path: '/xingniu/admin/:rest(.*)+', redirect: '/ops/admin' },
  // 旧运营端四屏（U1-1b）→ /ops：模板 → 选模板；任务单 → 出片；新建任务 → 选模板；看片判定 → 该条出片页（同一个运行 id）
  { path: '/xingniu/templates', name: 'XingniuTemplates', redirect: '/ops/templates' },
  { path: '/xingniu/runs', name: 'XingniuRuns', redirect: '/ops/runs' },
  { path: '/xingniu/runs/new', name: 'XingniuNewRun', redirect: '/ops/templates' },
  { path: '/xingniu/runs/:id', name: 'XingniuRunDetail', redirect: (to) => `/ops/runs/${encodeURIComponent(to.params.id)}` }
]
